import { UnifiedPharmacy } from './unified-pharmacy.model';

/**
 * Unified pharmacy catalogue — ONE Firestore subcollection for every integrator:
 *   Configuration/ClinicalPharmacies/ClinicalPharmacies/{docId}
 * one doc per pharmacy, tagged with `vendorId`, so the picker reads a single paginated,
 * server-filterable source instead of merging per-vendor docs in the app.
 */
export const CLINICAL_PHARMACIES_PARENT_DOC = 'ClinicalPharmacies';
export const CLINICAL_PHARMACIES_SUBCOLLECTION = 'ClinicalPharmacies';
/** `Configuration/ClinicalPharmacies/ClinicalPharmacies` (3 segments = a collection path). */
export const CLINICAL_PHARMACIES_PATH = `Configuration/${CLINICAL_PHARMACIES_PARENT_DOC}/${CLINICAL_PHARMACIES_SUBCOLLECTION}`;
/** The catalogue's parent doc (`Configuration/ClinicalPharmacies`, 2 segments) — holds per-vendor sync metadata. */
export const CLINICAL_PHARMACIES_META_DOC = `Configuration/${CLINICAL_PHARMACIES_PARENT_DOC}`;

/** Per-vendor sync metadata, stored as a `syncMeta` map (keyed by vendorId) on the parent doc. */
export interface PharmacySyncMeta {
  vendorLabel: string;
  count: number;
  lastSyncedAt: any; // Firestore Timestamp on read; serverTimestamp() sentinel on write
  byUserId?: string;
  byUsername?: string;
  byFullName?: string;
}

/** One stored pharmacy document. */
export interface ClinicalPharmacyDoc {
  docId: string; // `${vendorId}_${id}` — also the Firestore document id (queried via `in` for favourites)
  vendorId: string;
  vendorLabel: string;
  id: string; // original per-vendor id (Dischem siteNo / ComputAssist ramsno)
  name: string;
  nameLower: string; // orderBy + cursor pagination key (text search)
  addressLine: string;
  secondary?: { label: string; value: string };
  secondaryDigits?: string; // digits of secondary.value (phone / RAMS / BHF) — orderBy key for numeric search; omitted when empty
  province?: string;
  town?: string;
  raw: any; // original vendor record — needed by descriptor.send()
}

export function clinicalPharmacyDocId(vendorId: string, id: string): string {
  return `${vendorId}_${id}`;
}

/** Build the stored doc from a vendor-adapted UnifiedPharmacy + its vendor tag (ingestion time). */
export function toClinicalPharmacyDoc(p: UnifiedPharmacy, vendorId: string, vendorLabel: string): ClinicalPharmacyDoc {
  const secondaryDigits = (p.secondary?.value ?? '').replace(/\D/g, '');
  return {
    docId: clinicalPharmacyDocId(vendorId, p.id),
    vendorId,
    vendorLabel,
    id: p.id,
    name: p.name,
    nameLower: (p.name || '').toLowerCase(),
    addressLine: p.addressLine,
    secondary: p.secondary,
    ...(secondaryDigits ? { secondaryDigits } : {}),
    province: p.province,
    town: p.town,
    raw: p.raw,
  };
}

/** Map a stored doc back to the runtime UnifiedPharmacy the picker + send pipeline use. */
export function fromClinicalPharmacyDoc(doc: ClinicalPharmacyDoc, isFavourite = false): UnifiedPharmacy {
  return {
    id: doc.id,
    name: doc.name,
    secondary: doc.secondary,
    addressLine: doc.addressLine,
    province: doc.province,
    town: doc.town,
    isFavourite,
    // client-side search matches against this string: name + address (incl. town) + province +
    // secondary value (phone/RAMS/BHF) AND its digits — so name / address-word / province / phone
    // queries all work. (email isn't stored on the catalogue doc, so it isn't searchable here.)
    searchText: [doc.nameLower, doc.addressLine, doc.province, doc.secondary?.value, doc.secondaryDigits]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
    raw: doc.raw,
    vendorId: doc.vendorId,
    vendorLabel: doc.vendorLabel,
  };
}
